import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { SetFileConfig } from 'services/file.service';
import { AppThunk } from 'store/store';
import {
  HandleClips,
  HandleMarkers,
  HandleClipConfig,
  HandleStoryConfig,
  setBackupListener,
} from 'components/app/editor/_mediator/editor.slice';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { dispatchMonitor } from 'components/app/editor/_mediator/monitor/monitor.slice';

export enum BackupHandlers {
  handleClips,
  handleMarkers,
  handleFileConfigs,
  handleClipConfig,
  handleStoryConfig,
}

export interface BackupMap {
  [BackupHandlers.handleClips]: HandleClips;
  [BackupHandlers.handleMarkers]: HandleMarkers;
  [BackupHandlers.handleFileConfigs]: SetFileConfig;
  [BackupHandlers.handleClipConfig]: HandleClipConfig;
  [BackupHandlers.handleStoryConfig]: HandleStoryConfig;
}

export type SpecificBackups = {
  [H in BackupHandlers]: BaseBackup<BackupMap[H]> & { handler: H };
};

export interface BaseBackup<TData> {
  handler: BackupHandlers;
  data: TData;
  params?: BackupParams;
}

export type BackupReq<H extends BackupHandlers> = {
  data: BackupMap[H];
  backupParams?: BackupParams;
  backupAction?: BackupAction;
  backupSkip?: boolean;
};

export interface BackupParams {
  title: BackupTitle;
  action?: 'addClip' | 'delClip' | 'resizeRight';
  activeClip?: [CellClip, CellClip];
  selectedClips?: [ClipId[], ClipId[]];
  selectedMarkers?: [Index[], Index[]];
}

export type BackupTitle =
  | 'File Config'
  | 'File Effects'
  | 'File Rotate'
  | 'Clip Configs'
  | 'Clip Speed'
  | 'Clip Effects'
  | 'Clip Rotate'
  | 'Clip Pan/Zoom'
  | 'Clip Resize Shift'
  | 'Clip Resize'
  | 'Range Markers'
  | 'Cull Markers'
  | 'Delete Markers'
  | 'Add Markers'
  | 'Reset Markers'
  | 'DnD Markers'
  | 'Add Image'
  | 'Add Clip'
  | 'Delete Clip'
  | 'DnD Clip'
  | 'Link Clips'
  | 'Timestamp Clip'
  | 'Unlink Clips'
  | 'Story Config'
  | 'Story Title/Credits';
export type BackupAction = 'undo' | 'redo';
export type BackupUnion = SpecificBackups[BackupHandlers];
export type Backup = BackupUnion;

interface State {
  index: number;
  backups: Backup[];
}

const initialState: State = {
  index: 0,
  backups: [],
};

// T H U N K S

export const addBackup =
  (backup: Backup, backupReq: BackupReq<BackupHandlers>): AppThunk =>
  async (dispatch) => {
    const backupAction = backupReq.backupAction;
    const currPayload = { handler: backup.handler, data: backupReq.data, params: backup.params } as Backup;
    const logAction = backupAction ? `[${backupAction}] ` : '';
    dispatch(backupSlice.actions.handleBackup({ backup, currPayload, backupAction }));
    dispatch(setRecentAction({ key: 'BACKUP', desc: logAction + backupReq.backupParams?.title }));
    dispatch(dispatchMonitor({ backup }));
    dispatch(setBackupListener(backupAction));
  };
//

// S L I C E

export const backupSlice = createSlice({
  name: 'backup',
  initialState,
  reducers: {
    handleBackup: (
      state,
      action: PayloadAction<{ backup: Backup; currPayload: Backup; backupAction?: BackupAction }>
    ) => {
      const { backup, currPayload, backupAction } = action.payload;
      switch (backupAction) {
        case 'undo':
          if (state.index <= 0) return;
          state.backups[state.index] = backup;
          state.index--;
          break;
        case 'redo':
          if (state.index >= state.backups.length - 1) return;
          state.backups[state.index] = backup;
          state.index++;
          break;
        default:
          state.backups.splice(state.index);
          state.backups.push(backup, currPayload);
          state.index++;
      }
    },
    resetBackup: () => {
      return initialState;
    },
  },
});

export const { resetBackup } = backupSlice.actions;
export default backupSlice.reducer;
